import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Send, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { submitIntakeEnquiry } from '../services/api';

const HelpAHorsePage = () => {
  const [formData, setFormData] = useState({
    ownerName: '',
    email: '',
    phone: '',
    horseName: '',
    horseAge: '',
    horseBreed: '',
    reason: '',
    urgency: '',
    additionalInfo: '',
    consent: false
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await submitIntakeEnquiry(formData);
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit enquiry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (submitted) {
    return (
      <main>
        <div className="page-header">
          <div className="container">
            <h1 className="display-medium">Enquiry Received</h1>
          </div>
        </div>
        <section className="section">
          <div className="container" style={{ maxWidth: '600px' }}>
            <div className="success-message" style={{ padding: '48px', textAlign: 'center' }}>
              <CheckCircle2 size={48} color="#10b981" style={{ marginBottom: '16px' }} />
              <h2 className="heading-2" style={{ marginBottom: '16px' }}>Thank You</h2>
              <p className="body-medium" style={{ marginBottom: '24px' }}>
                We have received your enquiry and will be in touch within 48 hours. If your situation is urgent, please call us directly.
              </p>
              <Link to="/" className="btn-primary">Return to Home</Link>
            </div>
          </div>
        </section>
      </main>
    );
  }

  return (
    <main>
      <div className="page-header">
        <div className="container">
          <div className="breadcrumb">
            <Link to="/">Home</Link>
            <ChevronRight size={16} />
            <span>Help a Horse</span>
          </div>
          <h1 className="display-medium">Get Help for a Horse</h1>
          <p className="body-large">
            If you can no longer care for your horse, we&apos;re here to help – without judgement.
          </p>
        </div>
      </div>

      <section className="section">
        <div className="container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '64px' }}>
            {/* Info Panel */}
            <div>
              <h2 className="heading-2" style={{ marginBottom: '24px' }}>How We Can Help</h2>
              <p className="body-medium" style={{ marginBottom: '24px' }}>
                We understand that life changes. Financial hardship, health issues, bereavement, or simply changing circumstances can make it impossible to continue caring for your horse.
              </p>
              <p className="body-medium" style={{ marginBottom: '24px' }}>
                The Grazing Trust offers a compassionate, non-judgemental service for owners who need to find a safe future for their horse.
              </p>

              <div 
                style={{ 
                  background: 'white', 
                  padding: '24px', 
                  borderRadius: '16px', 
                  border: '1px solid var(--border-medium)',
                  marginBottom: '24px'
                }}
              >
                <h3 className="heading-3" style={{ marginBottom: '16px' }}>What Happens Next?</h3>
                <ol style={{ paddingLeft: '20px', marginBottom: '0' }}>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>We&apos;ll review your enquiry and contact you within 48 hours</li>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>We&apos;ll discuss your horse&apos;s needs and your situation</li>
                  <li className="body-medium" style={{ marginBottom: '12px' }}>If we can help, we&apos;ll arrange an assessment visit</li>
                  <li className="body-medium">Together, we&apos;ll agree on the best path forward</li>
                </ol>
              </div>

              <div 
                style={{ 
                  background: '#FEF3C7', 
                  padding: '20px', 
                  borderRadius: '12px', 
                  display: 'flex', 
                  gap: '12px'
                }}
              >
                <AlertCircle size={24} color="#92400E" style={{ flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <p className="body-small" style={{ color: '#92400E', fontWeight: 600 }}>
                    Emergency Welfare Concerns
                  </p>
                  <p className="body-small" style={{ color: '#92400E' }}>
                    If a horse is in immediate danger, please contact the SSPCA on 03000 999 999 or your local authority.
                  </p>
                </div>
              </div>
            </div>

            {/* Form */}
            <div 
              className="network-card" 
              style={{ padding: '40px' }}
            >
              <h2 className="heading-2" style={{ marginBottom: '8px' }}>Intake Enquiry Form</h2>
              <p className="body-small" style={{ marginBottom: '32px' }}>
                All information is treated confidentially.
              </p>

              {error && (
                <div 
                  style={{ 
                    background: '#FEE2E2', 
                    padding: '16px', 
                    borderRadius: '12px', 
                    marginBottom: '24px',
                    color: '#DC2626'
                  }}
                >
                  {error}
                </div>
              )}

              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <Label htmlFor="ownerName">Your Name *</Label>
                    <Input 
                      id="ownerName" 
                      value={formData.ownerName}
                      onChange={(e) => handleInputChange('ownerName', e.target.value)}
                      required
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input 
                      id="email" 
                      type="email"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      required
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input 
                    id="phone" 
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    style={{ marginTop: '8px' }}
                  />
                </div>

                <hr style={{ border: 'none', borderTop: '1px solid var(--border-medium)', margin: '32px 0' }} />

                <h3 className="heading-3" style={{ marginBottom: '20px' }}>About the Horse</h3>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <Label htmlFor="horseName">Horse&apos;s Name *</Label>
                    <Input 
                      id="horseName" 
                      value={formData.horseName}
                      onChange={(e) => handleInputChange('horseName', e.target.value)}
                      required
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                  <div className="form-group" style={{ margin: 0 }}>
                    <Label htmlFor="horseAge">Approximate Age</Label>
                    <Input 
                      id="horseAge" 
                      value={formData.horseAge}
                      onChange={(e) => handleInputChange('horseAge', e.target.value)}
                      placeholder="e.g. 12 years"
                      style={{ marginTop: '8px' }}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <Label htmlFor="horseBreed">Breed / Type</Label>
                  <Input 
                    id="horseBreed" 
                    value={formData.horseBreed}
                    onChange={(e) => handleInputChange('horseBreed', e.target.value)}
                    placeholder="e.g. Cob, Thoroughbred, Native Pony"
                    style={{ marginTop: '8px' }}
                  />
                </div>

                <div className="form-group">
                  <Label>Reason for Enquiry *</Label>
                  <Select 
                    value={formData.reason} 
                    onValueChange={(value) => handleInputChange('reason', value)}
                  >
                    <SelectTrigger style={{ marginTop: '8px' }}>
                      <SelectValue placeholder="Please select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="financial">Financial difficulties</SelectItem>
                      <SelectItem value="health">Owner health issues</SelectItem>
                      <SelectItem value="bereavement">Bereavement</SelectItem>
                      <SelectItem value="circumstances">Change in circumstances</SelectItem>
                      <SelectItem value="behavioural">Horse behavioural issues</SelectItem>
                      <SelectItem value="horse-health">Horse health issues</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label>Urgency</Label>
                  <Select 
                    value={formData.urgency} 
                    onValueChange={(value) => handleInputChange('urgency', value)}
                  >
                    <SelectTrigger style={{ marginTop: '8px' }}>
                      <SelectValue placeholder="How soon do you need help?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="immediate">Immediate (within days)</SelectItem>
                      <SelectItem value="weeks">Within a few weeks</SelectItem>
                      <SelectItem value="months">Within a few months</SelectItem>
                      <SelectItem value="planning">Planning ahead</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-group">
                  <Label htmlFor="additionalInfo">Additional Information</Label>
                  <Textarea 
                    id="additionalInfo" 
                    value={formData.additionalInfo}
                    onChange={(e) => handleInputChange('additionalInfo', e.target.value)}
                    placeholder="Please tell us anything else that would help us understand your situation..."
                    style={{ marginTop: '8px', minHeight: '120px' }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', marginBottom: '32px' }}>
                  <Checkbox 
                    id="consent"
                    checked={formData.consent}
                    onCheckedChange={(checked) => handleInputChange('consent', checked)}
                    style={{ marginTop: '3px' }}
                  />
                  <Label htmlFor="consent" style={{ fontWeight: 400, fontSize: '14px', lineHeight: 1.5 }}>
                    I understand that submitting this form does not guarantee intake. The Grazing Trust will assess each case individually and contact me to discuss options. *
                  </Label>
                </div>

                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={!formData.consent || !formData.reason || loading}
                  style={{ width: '100%', opacity: (formData.consent && formData.reason && !loading) ? 1 : 0.5 }}
                >
                  {loading ? (
                    <>
                      <Loader2 size={18} style={{ marginRight: '8px', animation: 'spin 1s linear infinite' }} />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <Send size={18} style={{ marginRight: '8px' }} />
                      Submit Enquiry
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </main>
  );
};

export default HelpAHorsePage;
